import React from 'react';
import { Box, Skeleton, Grid, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { designTokens } from '../theme/designTokens';

export const FinancialSummarySkeleton: React.FC = () => (
    <Grid container spacing={3} sx={{ mb: 4 }}>
        {[1, 2, 3].map((item) => (
            <Grid item xs={12} sm={4} key={item}>
                <Card sx={{
                    borderRadius: designTokens.borderRadius.lg,
                    bgcolor: designTokens.colors.background.paper,
                    border: designTokens.glassmorphism.border,
                    height: 140,
                    boxShadow: designTokens.shadows.md
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                            <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                            <Skeleton variant="text" width="60%" height={24} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                        </Box>
                        <Skeleton variant="rectangular" width="40%" height={32} sx={{ bgcolor: designTokens.colors.background.elevated, borderRadius: 1, mb: 1 }} />
                        <Skeleton variant="text" width="50%" height={16} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                    </CardContent>
                </Card>
            </Grid>
        ))}
    </Grid>
);

export const StatsGridSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
    <Grid container spacing={3} mb={4}>
        {Array.from({ length: count }).map((_, item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
                <Card sx={{
                    borderRadius: designTokens.borderRadius.lg,
                    bgcolor: designTokens.colors.background.paper,
                    border: designTokens.glassmorphism.border,
                    height: 140,
                    boxShadow: designTokens.shadows.md
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                            <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                            <Skeleton variant="text" width="50%" height={24} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                        </Box>
                        <Skeleton variant="rectangular" width="45%" height={36} sx={{ bgcolor: designTokens.colors.background.elevated, borderRadius: 1 }} />
                    </CardContent>
                </Card>
            </Grid>
        ))}
    </Grid>
);

export const ChartSkeleton: React.FC = () => (
    <Card sx={{
        borderRadius: designTokens.borderRadius.lg,
        bgcolor: designTokens.colors.background.paper,
        border: designTokens.glassmorphism.border,
        height: 400,
        boxShadow: designTokens.shadows.md
    }}>
        <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Skeleton variant="text" width="30%" height={32} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Skeleton variant="rectangular" width={60} height={28} sx={{ bgcolor: designTokens.colors.background.elevated, borderRadius: 1 }} />
                    <Skeleton variant="rectangular" width={60} height={28} sx={{ bgcolor: designTokens.colors.background.elevated, borderRadius: 1 }} />
                </Box>
            </Box>
            <Box sx={{ position: 'relative', height: 280, width: '100%', mt: 2 }}>
                {/* Simulated Grid Lines */}
                {[0, 1, 2, 3, 4].map((i) => (
                    <Box key={i} sx={{
                        position: 'absolute',
                        bottom: `${i * 25}%`,
                        width: '100%',
                        height: '1px',
                        bgcolor: designTokens.colors.background.elevated,
                        opacity: 0.3
                    }} />
                ))}
                {/* Simulated Chart Bars/Lines */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 2,
                    height: '100%',
                    px: 2
                }}>
                    {[40, 70, 45, 90, 65, 80, 50, 85].map((h, i) => (
                        <Skeleton
                            key={i}
                            variant="rectangular"
                            sx={{
                                flex: 1,
                                height: `${h}%`,
                                bgcolor: designTokens.colors.background.elevated,
                                opacity: 0.5,
                                borderRadius: '4px 4px 0 0'
                            }}
                        />
                    ))}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

export const LoadingSkeleton: React.FC = () => {
    return (
        <Box sx={{
            p: { xs: 2, sm: 3, md: 4 },
            maxWidth: '1600px',
            margin: '0 auto',
            width: '100%',
        }}>
            <FinancialSummarySkeleton />
            <StatsGridSkeleton count={4} />
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <ChartSkeleton />
                </Grid>
                <Grid item xs={12} md={6}>
                    <ChartSkeleton />
                </Grid>
            </Grid>
        </Box>
    );
};

export const DashboardSkeleton = LoadingSkeleton;

export const TableSkeleton: React.FC = () => {
    return (
        <TableContainer
            component={Paper}
            sx={{
                borderRadius: designTokens.borderRadius.lg,
                bgcolor: designTokens.colors.background.paper,
                border: designTokens.glassmorphism.border,
                boxShadow: designTokens.shadows.md,
                overflow: 'hidden',
                mt: 3
            }}
        >
            <Box sx={{ p: 3, borderBottom: `1px solid ${designTokens.colors.background.elevated}55`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Skeleton variant="text" width="25%" height={40} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Skeleton variant="rectangular" width={100} height={36} sx={{ bgcolor: designTokens.colors.background.elevated, borderRadius: 2 }} />
                    <Skeleton variant="rectangular" width={100} height={36} sx={{ bgcolor: designTokens.colors.background.elevated, borderRadius: 2 }} />
                </Box>
            </Box>
            <Table>
                <TableHead sx={{ bgcolor: `${designTokens.colors.background.elevated}33` }}>
                    <TableRow>
                        {[1, 2, 3, 4, 5, 6].map((item) => (
                            <TableCell key={item}>
                                <Skeleton variant="text" width="70%" height={24} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((row) => (
                        <TableRow key={row}>
                            {[1, 2, 3, 4, 5, 6].map((cell) => (
                                <TableCell key={cell}>
                                    <Skeleton variant="text" width={cell === 2 ? "90%" : "60%"} height={20} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export const CardGridSkeleton: React.FC = () => {
    return (
        <Box sx={{ p: designTokens.spacing.lg, maxWidth: '1200px', mx: 'auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Skeleton variant="text" width="200px" height={40} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                <Skeleton variant="rectangular" width="150px" height={40} sx={{ bgcolor: designTokens.colors.background.elevated, borderRadius: designTokens.borderRadius.md }} />
            </Box>

            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: designTokens.borderRadius.lg, mb: 6, bgcolor: designTokens.colors.background.elevated }} />

            <Grid container spacing={3}>
                {[1, 2, 3].map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item}>
                        <Card sx={{
                            borderRadius: designTokens.borderRadius.lg,
                            border: designTokens.glassmorphism.border,
                            boxShadow: designTokens.shadows.md,
                            bgcolor: designTokens.colors.background.paper,
                            height: 180
                        }}>
                            <CardContent sx={{ pt: 3 }}>
                                <Box display="flex" justifyContent="space-between" mb={3}>
                                    <Skeleton variant="text" width="60%" height={32} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                                    <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                                </Box>
                                <Skeleton variant="text" width="80%" height={48} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export const InvestmentSkeleton: React.FC = () => {
    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: '1600px', margin: '0 auto', width: '100%' }}>
            {/* Overview Skeleton */}
            <Grid container spacing={3} mb={4}>
                {[1, 2, 3].map((item) => (
                    <Grid item xs={12} sm={4} key={item}>
                        <Card sx={{
                            borderRadius: designTokens.borderRadius.lg,
                            bgcolor: designTokens.colors.background.paper,
                            border: designTokens.glassmorphism.border,
                            height: 120,
                            boxShadow: designTokens.shadows.md
                        }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                                <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: designTokens.colors.background.elevated, mb: 1.5 }} />
                                <Skeleton variant="text" width="50%" height={20} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                                <Skeleton variant="text" width="70%" height={28} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Main Content Skeleton - Portfolio/Table */}
            <Card sx={{
                borderRadius: designTokens.borderRadius.lg,
                bgcolor: designTokens.colors.background.paper,
                border: designTokens.glassmorphism.border,
                boxShadow: designTokens.shadows.md,
                mb: 4
            }}>
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                        <Skeleton variant="text" width="20%" height={32} sx={{ bgcolor: designTokens.colors.background.elevated }} />
                        <Skeleton variant="rectangular" width={120} height={32} sx={{ bgcolor: designTokens.colors.background.elevated, borderRadius: 1 }} />
                    </Box>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {[1, 2, 3, 4, 5].map(i => <TableCell key={i}><Skeleton variant="text" width="60%" height={20} sx={{ bgcolor: designTokens.colors.background.elevated }} /></TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[1, 2, 3, 4, 5].map(row => (
                                <TableRow key={row}>
                                    {[1, 2, 3, 4, 5].map(cell => <TableCell key={cell}><Skeleton variant="text" width="80%" height={18} sx={{ bgcolor: designTokens.colors.background.elevated }} /></TableCell>)}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Simulation/Analytics Skeleton */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <ChartSkeleton />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ borderRadius: designTokens.borderRadius.lg, bgcolor: designTokens.colors.background.paper, border: designTokens.glassmorphism.border, height: 400, boxShadow: designTokens.shadows.md }}>
                        <CardContent sx={{ p: 3 }}>
                            <Skeleton variant="text" width="40%" height={32} sx={{ bgcolor: designTokens.colors.background.elevated, mb: 3 }} />
                            {[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rectangular" height={60} sx={{ bgcolor: designTokens.colors.background.elevated, borderRadius: 2, mb: 2 }} />)}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

